// ProjectPeek — the one-level overlay opened from the projects list (ADR-036 §2).
// URL-driven (?peek=projects:<id>, useEntityPeek), rendered by PlatformPeekHost
// (the platform frame's host — the project-scoped PeekHost's sibling; P uplift).
// A few condensed read-only sections; the acts are the project's ONE registry
// (`ProjectActions.tsx` — the SAME set as the page): the top row Open project + the
// Edit menu, the footer's door the lifecycle beside "Open full page".

import { useTranslation } from 'react-i18next';
import { usePlatformProject } from '../../../hooks/usePlatformProjects.js';
import { useProjectActions } from './ProjectActions.js';
import { ContextPanel } from '../../../components/patterns/ContextPanel.js';
import { DetailSection } from '../../../components/patterns/DetailSection.js';
import { DescriptionList, Field } from '../../../components/patterns/DescriptionList.js';
import { StatStrip } from '../../../components/patterns/StatStrip.js';
import { Alert } from '../../../components/ui/Alert.js';
import { PageLoader } from '../../../components/ui/PageLoader.js';
import { countryName, formatDate, formatNumber } from '../../../lib/format.js';
import { useDisplayZone } from '../../../hooks/useDisplayZone.js';
import {
  AddonChips,
  BrandDot,
  LicensingSummary,
  StatusBadge,
  daysUntil,
  effectiveStatus,
  useTypeLabel,
} from '../../../components/patterns/projectDisplay.js';

export function ProjectPeek({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation('platform-projects');
  // The screen's one zone (ADR-082 §2): the viewer's own — a platform / personal page.
  const display = useDisplayZone({ kind: 'person' });
  const zone = display.zone ?? 'UTC';
  const typeLabel = useTypeLabel();
  const query = usePlatformProject(projectId);
  const project = query.data;
  const { acts, overlays } = useProjectActions(project);

  if (!project) {
    return (
      <ContextPanel host="peek" onClose={onClose} title={t('title')}>
        {query.isError ? <Alert variant="danger">{t('detail.error')}</Alert> : <PageLoader />}
      </ContextPanel>
    );
  }

  const status = effectiveStatus(project);
  const statusAlert = (() => {
    const d = project.trial_expires_at ? (formatDate(project.trial_expires_at, 'date', { timeZone: zone }) ?? '') : '';
    switch (status) {
      case 'trial':
        return <Alert variant="info">{t('peek.trial', { count: daysUntil(project.trial_expires_at!), date: d })}</Alert>;
      case 'trial-expired':
        return <Alert variant="warning">{t('peek.trialExpired', { date: d })}</Alert>;
      case 'suspended':
        return <Alert variant="warning">{t('peek.suspended')}</Alert>;
      case 'terminated':
        return <Alert variant="warning">{t('peek.terminated')}</Alert>;
      default:
        return null;
    }
  })();

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={<BrandDot project={project} size={12} />}
        kind={typeLabel(project.type)}
        title={project.display_name ?? project.name}
        status={<StatusBadge status={status} />}
        openFullPath={`/platform/projects/${project.id}`}
        acts={acts}
      >
        <div className="flex flex-col gap-3.5">
          {statusAlert}
          <StatStrip
            stats={[
              { label: t('kpi.members'), value: formatNumber(project.member_count) },
              { label: t('kpi.active30d'), value: formatNumber(project.active_member_count_30d) },
              { label: t('kpi.activeSites'), value: formatNumber(project.active_site_count) },
            ]}
          />
          <DetailSection title={t('detail.sections.identity')} dense>
            <DescriptionList>
              <Field label={t('fields.code')}>{project.name}</Field>
              <Field label={t('fields.motherCompany')}>{project.mother_company ?? '—'}</Field>
              <Field label={t('fields.serviceProvider')}>{project.service_provider ?? '—'}</Field>
              {project.countries.length <= 1 ? (
                <Field label={t('fields.country')}>{project.countries[0] ? countryName(project.countries[0], i18n.language) : '—'}</Field>
              ) : (
                <Field label={t('fields.countries')}>
                  {countryName(project.countries[0] ?? '', i18n.language)}
                  <span className="font-normal text-[var(--color-text-secondary)]"> {t('list.countriesOthers', { count: project.countries.length - 1 })}</span>
                </Field>
              )}
              <Field label={t('fields.created')}>{formatDate(project.created_at, 'date', { timeZone: zone })}</Field>
            </DescriptionList>
          </DetailSection>
          <DetailSection title={t('detail.licensing')} dense>
            <LicensingSummary project={project} dense />
          </DetailSection>
          <DetailSection title={t('detail.addons')} dense>
            <AddonChips project={project} />
          </DetailSection>
          {project.description && <p className="text-sm text-[var(--color-text-secondary)]">{project.description}</p>}
        </div>
      </ContextPanel>
      {overlays}
    </>
  );
}
